<template>

  <main class="fifa-day-page">

    <!-- سرصفحهٔ هاب -->
    <section class="hub-hero">

      <h1>فیفا دی</h1>

      <p class="lead">
        پنجرهٔ رسمی بازی‌های تیم‌های ملی — برنامه و نتایج مقدماتی جام جهانی،
        لیگ ملت‌های اروپا و بازی‌های دوستانه، روزبه‌روز به وقت تهران.
      </p>

      <div
          v-if="competitions.length"
          class="comp-chips"
      >

        <router-link
            v-for="c in competitions"
            :key="c.id"
            :to="`/competition/${c.id}`"
            class="comp-chip"
        >

          <img
              v-if="c.logo"
              :src="c.logo"
              :alt="c.name"
          >

          <span>{{ c.name }}</span>

        </router-link>

      </div>

    </section>

    <!-- روزشمار بازی‌ها -->
    <template v-if="days.length">

      <section
          v-for="day in days"
          :key="day.date"
          class="day-block"
      >

        <h3 class="day-title">

          <span class="day-badge">{{ dayLabel(day.date) }}</span>

        </h3>

        <article
            v-for="match in day.matches"
            :key="match.id"
            class="match-card"
            :class="{ live: isLive(match) }"
            @click="$router.push(`/match/${match.id}`)"
        >

          <div class="meta">

            <span
                v-if="match.competition"
                class="comp"
            >{{ match.competition.name }}</span>

            <span
                v-if="roundLabel(match.round)"
                class="round"
            >{{ roundLabel(match.round) }}</span>

            <span class="kickoff">{{ timeOnly(match) }}</span>

          </div>

          <div class="row">

            <div class="team">

              <img
                  :src="match.home.logo"
                  :alt="match.home.name"
              >

              <span>{{ teamName(match.home.name) }}</span>

            </div>

            <div class="center">

              <strong
                  v-if="isLive(match)"
                  class="live-score"
              >{{ match.home.score ?? 0 }} - {{ match.away.score ?? 0 }}</strong>

              <strong v-else-if="isFinished(match)">
                {{ match.home.score ?? 0 }} - {{ match.away.score ?? 0 }}
              </strong>

              <strong v-else>{{ timeOnly(match) }}</strong>

              <small :class="{ 'live-label': isLive(match) }">
                {{ isLive(match) ? '🔴 زنده' : statusLabel(match.status) }}
              </small>

            </div>

            <div class="team">

              <img
                  :src="match.away.logo"
                  :alt="match.away.name"
              >

              <span>{{ teamName(match.away.name) }}</span>

            </div>

          </div>

        </article>

      </section>

    </template>

    <!-- حالت خالی -->
    <div
        v-else-if="loaded"
        class="empty-state"
    >
      ⚽ فعلاً بازی ملی‌ای در پنجرهٔ جاری ثبت نشده — برنامهٔ پنجرهٔ بعدی همین‌جا منتشر می‌شود
    </div>

    <div
        v-else
        class="empty-state"
    >
      در حال بارگذاری…
    </div>

  </main>

</template>

<script>

import api from '@/services/api'
import { setSeo } from '@/utils/seo'
import { teamNames, roundLabel } from '@/utils/translations'

const LIVE_STATUSES = ['LIVE', 'IN_PLAY', 'PAUSED'];
const FINISHED_STATUSES = ['FINISHED', 'AWARDED'];

const STATUS_FA = {
  SCHEDULED: 'برنامه‌ریزی شده',
  TIMED: 'زمان‌بندی شده',
  LIVE: 'زنده',
  IN_PLAY: 'در جریان',
  PAUSED: 'استراحت',
  FINISHED: 'پایان یافته',
  POSTPONED: 'به تعویق افتاد',
  SUSPENDED: 'متوقف شد',
  CANCELLED: 'لغو شد',
  AWARDED: 'پایان یافته'
};

export default {

  name: 'FifaDay',

  data() {
    return {
      loaded: false,
      competitions: [],
      days: []
    }
  },

  async created() {

    setSeo({
      title: 'فیفا دی | برنامه و نتایج بازی‌های تیم‌های ملی | ضربه آزاد',
      description: 'برنامه و نتایج بازی‌های تیم‌های ملی در فیفا دی — مقدماتی جام جهانی، لیگ ملت‌های اروپا و بازی‌های دوستانه با روزشمار فارسی و ساعت تهران',
      path: '/fifa-day',
    });

    try {
      const data = await api.request('/fifa-day');
      this.competitions = data?.competitions || [];
      this.days = data?.days || [];
    } catch (e) {
      console.error('fifa-day load:', e);
    } finally {
      this.loaded = true;
    }

  },

  methods: {

    teamName(englishName) {
      return teamNames[englishName] || englishName || 'نامشخص';
    },

    roundLabel(value) {
      return roundLabel(value);
    },

    isLive(match) {
      return LIVE_STATUSES.includes(match.status);
    },

    isFinished(match) {
      return FINISHED_STATUSES.includes(match.status);
    },

    statusLabel(status) {
      return STATUS_FA[status] || status;
    },

    // برچسب فارسی روز (شمسی) برای سر هر بلوک
    dayLabel(dateKey) {
      try {
        return new Intl.DateTimeFormat(
            'fa-IR-u-ca-persian',
            { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Asia/Tehran' }
        ).format(new Date(`${dateKey}T12:00:00+03:30`));
      } catch (e) {
        return dateKey;
      }
    },

    timeOnly(match) {
      if (!match.kickoffAt) return '—';
      try {
        return new Intl.DateTimeFormat(
            'fa-IR',
            { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Tehran' }
        ).format(new Date(match.kickoffAt));
      } catch (e) {
        return '—';
      }
    }

  }

}

</script>

<style scoped>

.fifa-day-page {
  margin: auto;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}

/* ── سرصفحهٔ هاب ── */
.hub-hero {
  background: 
    url('~@/assets/images/header-ball-bg.svg') no-repeat left 4% center / 260px auto,
    linear-gradient(135deg, #022c22 0%, #064e3b 45%, #047857 85%, #059669 100%);
  border-radius: 20px;
  padding: 26px 24px;
  color: #fff;
  position: relative;
  overflow: hidden;

  @include mobile {
    background-position: left -30px center;
    background-size: 180px auto;
  }
}

.hub-hero h1 {
  margin: 0 0 10px;
  font-size: 26px;
}

.hub-hero .lead {
  margin: 0 0 14px;
  font-size: 14px;
  line-height: 1.9;
  color: #cbd5e1;
}

.comp-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.comp-chip {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, .1);
  border: 1px solid rgba(255, 255, 255, .22);
  color: #fff;
  text-decoration: none;
  padding: 7px 14px;
  border-radius: 999px;
  font-size: 12.5px;
  font-weight: 700;
  transition: .25s;
}

.comp-chip:hover {
  background: rgba(255, 255, 255, .2);
}

.comp-chip img {
  width: 20px;
  height: 20px;
  object-fit: contain;
}

/* ── روزشمار ── */
.day-block {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.day-title {
  margin: 6px 2px 0;
}

.day-badge {
  display: inline-block;
  background: #eff6ff;
  color: #1d4ed8;
  padding: 6px 16px;
  border-radius: 999px;
  font-size: 13.5px;
  font-weight: 800;
}

/* ── کارت مسابقه — هم‌زبان با کارت‌های صفحهٔ لیگ ── */
.match-card {
  background: #fff;
  border-radius: 18px;
  padding: 14px 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, .06);
  transition: .25s;
  cursor: pointer;
}

.match-card:hover {
  box-shadow: 0 8px 20px rgba(0, 0, 0, .1);
}

.match-card.live {
  border: 1px solid #fecaca;
}

.meta {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  font-size: 11.5px;
  color: #94a3b8;
  margin-bottom: 10px;
  padding-bottom: 10px;
  border-bottom: 1px dashed #e2e8f0;
}

.meta .comp {
  background: #f1f5f9;
  color: #475569;
  padding: 2px 10px;
  border-radius: 8px;
  font-weight: 700;
}

.meta .round {
  background: #fefce8;
  color: #a16207;
  padding: 2px 10px;
  border-radius: 8px;
  font-weight: 600;
}

.row {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  gap: 12px;
}

.team {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.team:last-child {
  flex-direction: row-reverse;
  text-align: left;
}

.team img {
  width: 38px;
  height: 38px;
  object-fit: contain;
  flex-shrink: 0;
}

.team span {
  font-weight: 600;
  font-size: 13.5px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.center {
  text-align: center;
  min-width: 84px;
}

.center strong {
  display: block;
  font-size: 19px;
  color: #0f172a;
}

.center small {
  color: #94a3b8;
  font-size: 12px;
}

.center small.live-label {
  color: #ef4444;
  font-weight: 700;
}

.live-score {
  color: #ef4444;
}

.empty-state {
  background: #fff;
  border-radius: 18px;
  padding: 60px 24px;
  text-align: center;
  color: #64748b;
  line-height: 2;
}

/* ── موبایل ── */
@media (max-width: 768px) {

  .fifa-day-page {
    padding: 14px 12px;
    gap: 14px;
  }

  .hub-hero {
    padding: 20px 16px;
    border-radius: 16px;
  }

  .hub-hero h1 {
    font-size: 21px;
  }

  .hub-hero .lead {
    font-size: 12.5px;
  }

  .row {
    grid-template-columns: 1fr;
    text-align: center;
  }

  .team,
  .team:last-child {
    justify-content: center;
    flex-direction: column;
    gap: 6px;
  }

  .team span {
    white-space: normal;
    text-align: center;
  }

}

</style>
